import { StatCard, StatRow, type StatTrend } from "@gladis/ui";
import { useT } from "@gladis/ui/i18n";
import type { ReactNode } from "react";

import type { InvestmentsPageDto } from "../dto.js";
import { formatGain, formatMoney, formatPercent } from "../format.js";

/**
 * The four figures the page opens with.
 *
 * ==========================================================================================
 * THE UNPRICED NOTICE IS THE ONE THAT MATTERS
 * ==========================================================================================
 *
 * When the household holds a stock with no price, Total value and Unrealised gain are SHORT, and the
 * cards say so. They do not absorb the gap as a zero.
 *
 * This is the rule the Expenses page already applies to a missing mortgage figure, and it now binds
 * the dashboard too (AC-D9). A total that is quietly incomplete is worse than one that admits it: the
 * household reads a number, believes it, and has no way to discover it was wrong.
 *
 * REALISED GAIN HAS NO PERCENTAGE, deliberately. A percentage needs a base, and the base for money
 * already taken out is whatever it happened to cost — a figure that says nothing about how the
 * portfolio is doing now. Unrealised gain has an obvious base and gets one.
 */

function trendOf(value: string): StatTrend {
  const amount = Number(value);
  if (amount > 0) return "up";
  if (amount < 0) return "down";
  return "level";
}

export interface InvestmentsStatsProps {
  page: InvestmentsPageDto;
}

export function InvestmentsStats({ page }: InvestmentsStatsProps): ReactNode {
  const t = useT();
  const { totals } = page;
  const unpriced = totals.unpricedHoldingCount;

  const shortfall =
    unpriced === 0
      ? null
      : `Excludes ${unpriced} holding${unpriced === 1 ? "" : "s"} with no price`;

  return (
    <StatRow>
      <StatCard
        label={t("inv.stats.totalValue")}
        value={formatMoney(totals.totalValue)}
        hint={shortfall ?? `At prices as of ${page.asOf}`}
      />
      <StatCard
        label={t("inv.stats.totalInvested")}
        value={formatMoney(totals.totalInvested)}
        hint={t("inv.stats.totalInvestedHint")}
      />
      <StatCard
        label={t("inv.stats.unrealised")}
        value={formatGain(totals.unrealisedGain)}
        trend={trendOf(totals.unrealisedGain)}
        hint={shortfall ?? formatPercent(totals.unrealisedGainPercent)}
      />
      <StatCard
        label={t("inv.stats.realised")}
        value={formatGain(totals.realisedGain)}
        trend={trendOf(totals.realisedGain)}
        hint={t("inv.stats.realisedHint")}
      />
    </StatRow>
  );
}
